import dedent from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Provider,
  QueryAndOutputs,
  TableSchema,
  WorkflowCodeType,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType,
  WorkflowComponentParameterMode
} from '../types'
import { addColumn } from '../utils/schemas'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'

class H3KRing extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.h3kring',
        group: 'Spatial Indexes',
        title: 'H3 KRing',
        description: `
      Neighboring indexes in all directions under the K distance.
      `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'h3',
          type: WorkflowComponentParamType.Column,
          title: 'H3 index column',
          parent: 'source',
          dataType: [ColumnDataType.String]
        },
        {
          name: 'size',
          type: WorkflowComponentParamType.Number,
          title: 'Size',
          min: 1,
          default: 1,
          mode: WorkflowComponentParameterMode.Slider
        },
        {
          name: 'includecols',
          type: WorkflowComponentParamType.Boolean,
          title: 'Keep input table columns',
          default: true
        },
        {
          name: 'includedistance',
          type: WorkflowComponentParamType.Boolean,
          title: 'Include KRing distance column',
          default: true
        }
      ]
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    envVars,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    const dryRun = codeType === WorkflowCodeType.DryRun
    const outputTablename = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      'result',
      codeType
    )
    const preparedParams = this._prepareParameters(params)
    const namespace = `${connection.quoteName(
      envVars.analyticsToolboxDataset as string
    )}`
    const sourceT = `${connection.quoteName(preparedParams.source as string)}`
    let select = ''
    let includeDistance = ''
    let kringIndexClause = ''
    switch (connection.type) {
      case Provider.BigQuery:
        if (preparedParams.includedistance) {
          includeDistance = 'kring.distance AS kring_distance'
          if (preparedParams.includecols) {
            // adding "," because the clause is not at the end of SELECT clause
            includeDistance = includeDistance + ','
          }
        }

        kringIndexClause = `kring.index AS kring_index`
        // add ',' if no ending clause before FROM
        kringIndexClause +=
          preparedParams.includedistance || preparedParams.includecols
            ? ','
            : ''

        if (preparedParams.includecols) {
          select = `WITH __h3_kring AS
                      (
                        SELECT
                          CASE
                            WHEN ${preparedParams.h3} IS NULL
                            THEN
                              [STRUCT(CAST(NULL AS STRING) as index, 0 as distance)]
                            ELSE
                              ${namespace}.H3_KRING_DISTANCES(
                                  ${preparedParams.h3},
                                  ${preparedParams.size}
                              )
                          END
                          AS h3_kring,
                          *
                        FROM ${sourceT}
                        WHERE ${preparedParams.h3} IS NOT NULL
                      )
                    SELECT
                      ${kringIndexClause}
                      ${includeDistance}
                      allvalues.* except(h3_kring)
                    FROM
                      __h3_kring allvalues,
                      unnest(allvalues.h3_kring) kring`
        } else {
          select = `WITH __h3_kring AS
                      (
                        SELECT
                          CASE
                            WHEN ${preparedParams.h3} IS NULL
                            THEN
                              [STRUCT(CAST(NULL AS STRING) as index, 0 as distance)]
                            ELSE
                              ${namespace}.H3_KRING_DISTANCES(
                                  ${preparedParams.h3},
                                  ${preparedParams.size}
                              )
                          END
                          AS h3_kring,
                          *
                        FROM ${sourceT}
                        WHERE ${preparedParams.h3} IS NOT NULL
                      )
                    SELECT
                      allvalues.${preparedParams.h3},
                      ${kringIndexClause}
                      ${includeDistance}
                    FROM
                      __h3_kring allvalues,
                      unnest(allvalues.h3_kring) kring`
        }
        break
      case Provider.Snowflake:
        if (preparedParams.includedistance) {
          includeDistance = `JSON_EXTRACT_PATH_TEXT(new.value, 'distance') as kring_distance`
          if (preparedParams.includecols) {
            // adding "," because the clause is not at the end of SELECT clause
            includeDistance = includeDistance + ','
          }
        }

        kringIndexClause = `JSON_EXTRACT_PATH_TEXT(new.value, 'index') as kring_index`
        // add ',' if no ending claquse before FROM
        kringIndexClause +=
          preparedParams.includedistance || preparedParams.includecols
            ? ','
            : ''

        if (preparedParams.includecols) {
          select = `WITH __h3_kring AS
                    (
                      SELECT
                        CASE
                          WHEN ${preparedParams.h3} IS NULL
                          THEN
                            ARRAY_CONSTRUCT(
                              OBJECT_CONSTRUCT(
                                'index', NULL::STRING,
                                'distance', 0
                              )
                            )
                          ELSE
                            ${namespace}.H3_KRING_DISTANCES(
                                ${preparedParams.h3},
                                ${preparedParams.size}
                            )
                        END
                        AS h3_kring,
                        *
                      FROM ${sourceT}
                      WHERE ${preparedParams.h3} IS NOT NULL
                    )
                  SELECT
                    source.${preparedParams.h3},
                    ${kringIndexClause}
                    ${includeDistance}
                    source.* EXCLUDE(${preparedParams.h3})
                  FROM
                    ${sourceT} source,
                    __h3_kring kring,
                    lateral FLATTEN(input => kring.h3_kring) new
                  WHERE
                    source.${preparedParams.h3} = kring.${preparedParams.h3}`
        } else {
          select = `WITH __h3_kring AS
                  (
                    SELECT
                      CASE
                        WHEN ${preparedParams.h3} IS NULL
                        THEN
                          ARRAY_CONSTRUCT(
                            OBJECT_CONSTRUCT(
                              'index', NULL::STRING,
                              'distance', 0
                            )
                          )
                        ELSE
                          ${namespace}.H3_KRING_DISTANCES(
                              ${preparedParams.h3},
                              ${preparedParams.size}
                          )
                      END
                      AS h3_kring,
                      *
                    FROM ${sourceT}
                    WHERE ${preparedParams.h3} IS NOT NULL
                  )
                  SELECT
                    kring.${preparedParams.h3},
                    ${kringIndexClause}
                    ${includeDistance}
                  FROM
                    __h3_kring kring,
                    lateral FLATTEN(input => kring.h3_kring) new`
        }
        break
      case Provider.Postgres:
        if (preparedParams.includedistance) {
          includeDistance = `(unnest(kring.h3_kring)::json->>'distance')::int as kring_distance`
          if (preparedParams.includecols) {
            // adding "," because the clause is not at the end of SELECT clause
            includeDistance = includeDistance + ','
          }
        }
        kringIndexClause = `(unnest(kring.h3_kring)::json->>'index') as kring_index`
        // add ',' if no ending claquse before FROM
        kringIndexClause +=
          preparedParams.includedistance || preparedParams.includecols
            ? ','
            : ''

        if (preparedParams.includecols) {
          select = `WITH __h3_kring AS
                    (
                      SELECT
                        CASE
                          WHEN ${preparedParams.h3} IS NULL
                          THEN
                            ARRAY[
                              JSON_OBJECT('{index, null, distance, 0}')
                            ]
                          ELSE
                            ${namespace}.H3_KRING_DISTANCES(
                                ${preparedParams.h3},
                                ${preparedParams.size}
                            )
                        END
                        AS h3_kring,
                        *
                      FROM ${sourceT}
                      WHERE ${preparedParams.h3} IS NOT NULL
                    )
                    SELECT
                      ${kringIndexClause}
                      ${includeDistance}
                      source.*
                    FROM
                      ${sourceT} source,
                      __h3_kring kring
                    WHERE
                      source.${preparedParams.h3} = kring.${preparedParams.h3}`
        } else {
          select = `WITH __h3_kring AS
                    (
                      SELECT
                        CASE
                          WHEN ${preparedParams.h3} IS NULL
                          THEN
                            ARRAY[
                              JSON_OBJECT('{index, null, distance, 0}')
                            ]
                          ELSE
                            ${namespace}.H3_KRING_DISTANCES(
                                ${preparedParams.h3},
                                ${preparedParams.size}
                            )
                        END
                        AS h3_kring,
                        *
                      FROM ${sourceT}
                      WHERE ${preparedParams.h3} IS NOT NULL
                    )
                    SELECT
                      ${preparedParams.h3},
                      ${kringIndexClause}
                      ${includeDistance}
                    FROM
                      __h3_kring kring`
        }
        break
      case Provider.DatabricksRest:
        if (preparedParams.includedistance) {
          includeDistance = 'kring.distance AS kring_distance'
          if (preparedParams.includecols) {
            // adding "," because the clause is not at the end of SELECT clause
            includeDistance = includeDistance + ','
          }
        }

        kringIndexClause = `kring.cellid AS kring_index`
        // add ',' if no ending clause before FROM
        kringIndexClause +=
          preparedParams.includedistance || preparedParams.includecols
            ? ','
            : ''

        if (preparedParams.includecols) {
          select = `WITH __h3_kring AS
                        (
                          SELECT /*+ REPARTITION(40) */
                            EXPLODE(H3_KRINGDISTANCES(
                                    ${preparedParams.h3},
                                    ${preparedParams.size}
                            )) AS kring,
                            *
                          FROM ${sourceT}
                          WHERE ${preparedParams.h3} IS NOT NULL
                        )
                      SELECT /*+ REPARTITION(40) */
                        ${kringIndexClause}
                        ${includeDistance}
                        * except(kring)
                      FROM
                        __h3_kring`
        } else {
          select = `WITH __h3_kring AS
                        (
                          SELECT /*+ REPARTITION(40) */
                            EXPLODE(H3_KRINGDISTANCES(
                                ${preparedParams.h3},
                                ${preparedParams.size}
                            )) AS kring,
                            *
                          FROM ${sourceT}
                          WHERE ${preparedParams.h3} IS NOT NULL
                        )
                      SELECT /*+ REPARTITION(40) */
                        ${preparedParams.h3},
                        ${kringIndexClause}
                        ${includeDistance}
                      FROM
                        __h3_kring`
        }
        break
    }
    let query = connection.getCreateTempTableQuery(
      outputTablename,
      select,
      codeType,
      null
    )

    // make dryrun query available also if plv8 is not available
    if (connection.type === Provider.Postgres) {
      if (dryRun) {
        if (preparedParams.includedistance) {
          includeDistance = `0 as kring_distance`
          if (preparedParams.includecols) {
            // adding "," because the clause is not at the end of SELECT clause
            includeDistance = includeDistance + ','
          }
        }

        kringIndexClause = `'kring_index' AS kring_index`
        // add ',' if no ending clause before FROM
        kringIndexClause +=
          preparedParams.includedistance || preparedParams.includecols
            ? ','
            : ''

        if (preparedParams.includecols) {
          select = dedent`SELECT
                            ${kringIndexClause}
                            ${includeDistance}
                            *
                          FROM
                            ${sourceT}`
        } else {
          select = dedent`SELECT
                            'h3' as ${preparedParams.h3},
                            ${kringIndexClause}
                            ${includeDistance}`
        }

        query = connection.getCreateTempTableQuery(
          outputTablename,
          select,
          codeType,
          null
        )
      } else {
        query = dedent`IF EXISTS(SELECT 1 FROM pg_extension WHERE extname = 'plv8') THEN
                          ${query}
                        ELSE
                          RAISE EXCEPTION 'plv8 extension not installed';
                        END IF;`
      }
    }
    return {
      query,
      outputs: { result: outputTablename }
    }
  }

  requiredAnalyticsToolboxVersion(provider: Provider): string | null {
    switch (provider) {
      case Provider.Postgres:
        return '1.1.1'
      default:
        return '1.0.0'
    }
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns }: GetOutputSchemaParams): TableSchema {
    return addColumn(columns.source, {
      name: 'kring_index',
      type: ColumnDataType.String
    })
  }
  supportedProviders(): Provider[] {
    return [
      Provider.BigQuery,
      Provider.Snowflake,
      Provider.Postgres,
      Provider.DatabricksRest
    ]
  }
}

export { H3KRing }
